import { useEffect, useRef } from "react";
import { mandiriDirectDebitTerms } from "../content/mandiriDirectDebitTerms";

const termsBlocks = mandiriDirectDebitTerms.split(/\n\n+/).map((block) => {
  const indent = block.match(/^( +)/);
  const text = block.trim();
  return {
    text,
    heading: /^\*\*[^*]+\*\*$/.test(text),
    nested: Boolean(indent),
  };
});

function RichText({ text }) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={index} style={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
}

export default function TermsModal({ agreed, onAgreedChange, onClose }) {
  const dialogRef = useRef(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const onKey = (event) => {
      if (event.key === "Escape") {
        onCloseRef.current();
      }
    };
    document.addEventListener("keydown", onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return (
    <div
      className="dd-terms-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <style>{`
        .dd-terms-overlay {
          position: fixed;
          inset: 0;
          z-index: 20;
          background: rgba(30, 38, 53, 0.5);
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 16px;
          box-sizing: border-box;
        }
        .dd-terms-dialog {
          width: 100%;
          max-width: 600px;
          height: min(827px, calc(100vh - 32px));
          background: #FFFFFF;
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          min-height: 0;
          font-family: Rubik, sans-serif;
          outline: none;
        }
        .dd-terms-header,
        .dd-terms-footer {
          flex-shrink: 0;
          display: flex;
          align-items: center;
          justify-content: space-between;
          box-sizing: border-box;
        }
        .dd-terms-header {
          height: 52px;
          padding: 0 16px;
          border-bottom: 1px solid #E6E6E6;
        }
        .dd-terms-body {
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          padding: 8px 16px;
          box-sizing: border-box;
          scrollbar-width: thin;
          scrollbar-color: #C0C2C6 transparent;
        }
        .dd-terms-body::-webkit-scrollbar {
          width: 14px;
        }
        .dd-terms-body::-webkit-scrollbar-thumb {
          background-color: #C0C2C6;
          border-radius: 999px;
          border: 4px solid transparent;
          background-clip: padding-box;
        }
        .dd-terms-copy {
          margin: 0;
          font-size: 14px;
          font-weight: 400;
          line-height: 20px;
          color: #000000;
        }
        .dd-terms-copy p {
          margin: 0 0 8px;
        }
        .dd-terms-copy p:last-child {
          margin-bottom: 0;
        }
        .dd-terms-copy .dd-terms-heading {
          margin-top: 8px;
          font-weight: 700;
        }
        .dd-terms-copy .dd-terms-nested {
          padding-left: 16px;
        }
        .dd-terms-footer {
          gap: 8px;
          justify-content: flex-start;
          padding: 15px 16px 16px;
          border-top: 1px solid #E6E6E6;
          cursor: pointer;
        }
        @media (min-width: 1024px) {
          .dd-terms-overlay {
            padding: 32px 16px;
          }
          .dd-terms-dialog {
            height: min(827px, calc(100vh - 64px));
          }
          .dd-terms-header {
            padding: 0 24px;
          }
          .dd-terms-body {
            padding: 8px 24px;
          }
          .dd-terms-footer {
            padding: 15px 24px 16px;
          }
        }
      `}</style>
      <div
        ref={dialogRef}
        className="dd-terms-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dd-terms-title"
        tabIndex={-1}
      >
        <div className="dd-terms-header">
          <h2 id="dd-terms-title" style={titleStyle}>Terms & Conditions</h2>
          <button type="button" onClick={onClose} aria-label="Close" style={closeStyle}>
            <img
              src="/assets/images/direct-debit/close.svg"
              alt=""
              width={20}
              height={20}
              style={{ width: 20, height: 20, display: "block" }}
            />
          </button>
        </div>

        <div className="dd-terms-body">
          <div className="dd-terms-copy">
            {termsBlocks.map((block, index) => (
              <p
                key={index}
                className={block.heading ? "dd-terms-heading" : block.nested ? "dd-terms-nested" : undefined}
              >
                <RichText text={block.text} />
              </p>
            ))}
          </div>
        </div>

        <label className="dd-terms-footer">
          <span style={checkboxHitStyle}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={(event) => onAgreedChange(event.target.checked)}
              style={checkboxInputStyle}
            />
            <img
              src={agreed
                ? "/assets/images/direct-debit/check-box.svg"
                : "/assets/images/direct-debit/check-box-outline-blank.svg"}
              alt=""
              width={24}
              height={24}
              style={{ width: 24, height: 24, display: "block" }}
            />
          </span>
          <span style={agreeStyle}>I have read and agree to the Cooperation Agreement</span>
        </label>
      </div>
    </div>
  );
}

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 700,
  lineHeight: "19px",
  color: "#041C2C",
};

const closeStyle = {
  width: 20,
  height: 20,
  padding: 0,
  border: "none",
  background: "transparent",
  display: "block",
  cursor: "pointer",
};

const checkboxHitStyle = {
  position: "relative",
  width: 24,
  height: 24,
  flexShrink: 0,
};

const checkboxInputStyle = {
  position: "absolute",
  inset: 0,
  opacity: 0,
  margin: 0,
  cursor: "pointer",
};

const agreeStyle = {
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "21px",
  color: "#000000",
};
